// client/src/context/CartContext.js
import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";

export const CartContext = createContext();

const LS_KEY = "jamcan.cart.v1";

// Small helpers
const toNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const sanitizeLine = (line) => {
  // Ensure minimal valid shape when restoring from storage
  return {
    id: line?.id,
    name: line?.name ?? "",
    description: line?.description ?? "",
    spiceLevel: toNumber(line?.spiceLevel, 0),
    image: line?.image ?? "",
    size: line?.size ?? "",
    quantity: Math.max(1, toNumber(line?.quantity, 1)),
    price: Number(toNumber(line?.price, 0).toFixed(2)), // dollars
    priceId: line?.priceId ?? null,
    product: line?.product ?? null,
  };
};

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const hydrated = useRef(false); // prevent double-hydration in React 18 Strict Mode

  // --- Rehydrate once on mount ---
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setCartItems(parsed.map(sanitizeLine).filter((x) => x.id && x.size));
        }
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  // --- Persist on change ---
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(cartItems));
    } catch {
      // ignore quota errors
    }
  }, [cartItems]);

  // product = object from your catalog
  // selectedSize = "5oz" | "12oz" ...
  // qty = optional (default 1)
  const addToCart = (product, selectedSize, qty = 1) => {
    if (!product || !selectedSize) return;

    // Resolve Stripe Price ID for this size
    const priceId =
      product?.priceIdBySize?.[selectedSize] ||
      product?.sizes?.find?.((s) => s.label === selectedSize)?.priceId ||
      product?.priceId ||
      null;

    // Unit price (in dollars) for display
    const unitPrice =
      toNumber(product?.price?.[selectedSize]) ||
      toNumber(
        (product?.sizes?.find?.((s) => s.label === selectedSize)?.unitAmount ??
          0) / 100
      );

    setCartItems((prev) => {
      const idx = prev.findIndex(
        (it) => it.id === product.id && it.size === selectedSize
      );
      if (idx !== -1) {
        const copy = [...prev];
        copy[idx] = {
          ...copy[idx],
          quantity: (copy[idx].quantity || 0) + qty,
        };
        return copy;
      }
      return [
        ...prev,
        sanitizeLine({
          id: product.id,
          name: product.name,
          description: product.description,
          spiceLevel: product.spiceLevel,
          image: product.image,
          size: selectedSize,
          quantity: qty,
          price: Number(unitPrice.toFixed(2)),
          priceId,
          product,
        }),
      ];
    });
  };

  const incrementQuantity = (id, size) =>
    setCartItems((prev) =>
      prev.map((it) =>
        it.id === id && it.size === size
          ? { ...it, quantity: (it.quantity || 1) + 1 }
          : it
      )
    );

  const decrementQuantity = (id, size) =>
    setCartItems((prev) =>
      prev
        .map((it) =>
          it.id === id && it.size === size
            ? { ...it, quantity: Math.max(1, (it.quantity || 1) - 1) }
            : it
        )
        .filter(Boolean)
    );

  const setQuantity = (id, size, qty) =>
    setCartItems((prev) =>
      prev.map((it) =>
        it.id === id && it.size === size
          ? { ...it, quantity: Math.max(1, toNumber(qty, 1)) }
          : it
      )
    );

  const removeFromCart = (id, size) =>
    setCartItems((prev) =>
      prev.filter((it) => !(it.id === id && it.size === size))
    );

  const clearCart = () => {
    setCartItems([]);
    try {
      localStorage.removeItem(LS_KEY);
    } catch {}
  };

  // --- Derived totals ---
  const itemCount = useMemo(
    () => cartItems.reduce((sum, it) => sum + (it.quantity || 0), 0),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      Number(
        cartItems
          .reduce(
            (sum, it) => sum + toNumber(it.price, 0) * (it.quantity || 0),
            0
          )
          .toFixed(2)
      ),
    [cartItems]
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        incrementQuantity,
        decrementQuantity,
        setQuantity,
        removeFromCart,
        clearCart,
        itemCount,
        subtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

// Optional convenience hook
export const useCart = () => useContext(CartContext);
